/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 데이터 레코드 클래스를 정의한다.
 *
 * @file /src/models/store/Record.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 30.
 */
import { Stores } from '@models/store/Store';
import { Local as LocalProvider } from '@models/store/provider/Local';
import { Ajax, Results } from '@utils/Ajax';
import { Chunks } from '@utils/Chunks';
import { Format } from '@utils/Format';
import { UUID } from '@utils/UUID';

export class Record {
    /**
     * 레코드고유값
     */
    id: string;

    /**
     * 데이터
     */
    data: { [key: string]: any } = {};

    /**
     * 데이터 변경이 되었을 경우, 원본 데이터
     */
    origin: { [key: string]: any } = null;

    /**
     * 변경된 데이터
     */
    updated: { [key: string]: any } = null;

    /**
     * 레코드 고유값 필드명
     */
    primaryKeys: { [key: string]: any } = null;

    /**
     * 데이터 정렬방식
     */
    sorters: { [field: string]: 'ASC' | 'DESC' | any[] } = null;

    /**
     * 부모레코드
     */
    parent: Record = null;

    /**
     * 레코드 깊이
     */
    depth: number = null;

    /**
     * 자식레코드
     */
    children: boolean | Map<string, Record>;

    /**
     * 자식레코드 아이디(정렬 및 필터링된 아이디)
     */
    childIds: string[] = null;

    /**
     * 자식 데이터를 불러오고 있는 로더고유값
     */
    childLoaderId: string;

    /**
     * 자식데이터 로더
     */
    childLoaders: Map<string, Promise<{ [key: string]: any }[]>> = new Map();

    /**
     * 자식데이터 로더 결과데이터
     */
    childLoaderResults: Map<string, Results> = new Map();

    /**
     * 데이터 필터
     */
    filters: { [field: string]: { value: any; operator: string } } = null;

    /**
     * 필터모드
     */
    filterMode: 'OR' | 'AND' = 'AND';

    /**
     * 데이터 변경 후 실행할 함수
     */
    observer: (record: Record, fields: string[], tree: boolean) => void = null;

    /**
     * 데이터 레코드를 정의한다.
     *
     * @param data - 레코드데이터
     * @param store - 데이터스토어 인스턴스
     */
    constructor(
        data: { [key: string]: any },
        public store: Stores
    ) {
        this.data = {};

        const fields = this.store.fields;
        let children: { [key: string]: any }[] | boolean = false;
        for (const key in data) {
            if (fields[key] !== undefined) {
                const field = fields[key];
                if (field.type == 'children') {
                    children = data[key];
                    continue;
                }

                switch (field.type) {
                    case 'int':
                        this.data[key] = parseInt(data[key]);
                        break;

                    case 'float':
                        this.data[key] = parseFloat(data[key]);
                        break;

                    case 'string':
                        this.data[key] = data[key] !== null ? data[key].toString() : null;
                        break;

                    case 'boolean':
                        this.data[key] =
                            data[key] === true || data[key] === 1 || data[key] == 'true' || data[key] == 'TRUE';
                        break;

                    default:
                        this.data[key] = data[key];
                }

                if (field.is_primary == true) {
                    this.primaryKeys ??= {};
                    this.primaryKeys[key] = this.data[key];
                }
            } else {
                this.data[key] = data[key];
                if (key == 'children') {
                    children = data[key];
                    continue;
                }
            }
        }

        if (typeof children === 'boolean') {
            this.children = children;
        } else {
            if (children.length == 0) {
                this.children = false;
            } else {
                this.children = new Map();
                this.childIds = [];
                for (const childData of children) {
                    const child = this.store.set(childData, false);
                    child.setParent(this);
                    this.children.set(child.getId(), child);
                    this.childIds.push(child.getId());
                }
            }
        }

        this.id = this.getId();
    }

    /**
     * 부모 레코드를 저장한다.
     */
    setParent(record: Record): void {
        this.parent = record;
    }

    /**
     * 부모 레코드를 가져온다.
     */
    getParent(): Record {
        return this.parent;
    }

    /**
     * 레코드 고유값을 가져온다.
     *
     * @returns 고유값
     */
    getId(): string {
        if (this.id === undefined) {
            if (this.primaryKeys !== null) {
                this.id = UUID.v5('d723a13e-7796-58a2-b5d4-c8ac60759c70', JSON.stringify(this.primaryKeys));
            } else {
                this.id = UUID.v5('d723a13e-7796-58a2-b5d4-c8ac60759c70', JSON.stringify(this.data));
            }
        }

        return this.id;
    }

    /**
     * 특정필드의 데이터를 가져온다.
     *
     * @param field - 필드명
     * @returns 데이터
     */
    get(field: string): any {
        return this.data[field] ?? null;
    }

    /**
     * 특정필드의 데이터를 변경한다.
     *
     * @param field - 변경할 필드명
     * @param value - 변경할 데이터
     * @param is_fire_event - 이벤트 발생여부
     */
    async set(field: string, value: any, is_fire_event: boolean = true): Promise<void> {
        this.origin ??= { ...this.data };

        const updated = this.updated ?? {};

        if (Format.isEqual(this.origin[field], value) == false) {
            updated[field] = value;
        } else if (updated[field] !== undefined) {
            delete updated[field];
        }

        if (Object.keys(updated).length > 0) {
            this.updated = updated;
        } else {
            this.updated = null;
        }

        const isChanged = Format.isEqual(this.data[field], value) == false;
        if (isChanged == true) {
            this.data[field] = value;
        }

        await this.store.onEdit(this, is_fire_event);

        if (isChanged == true) {
            this.onUpdate({ [field]: value });
        }
    }

    /**
     * 모든 데이터를 가져온다.
     *
     * @returns 모든 데이터
     */
    getData(): { [key: string]: any } {
        return this.data;
    }

    /**
     * 모든 데이터를 재설정한다.
     *
     * @param data - 데이터
     * @param is_fire_event - 이벤트 발생여부
     */
    async setData(data: { [key: string]: any }, is_fire_event: boolean = true): Promise<void> {
        const recordId = this.getId();
        this.origin ??= { ...this.data };

        const updated = this.updated ?? {};

        for (const [field, value] of Object.entries(data)) {
            if (Format.isEqual(this.origin[field], value) == false) {
                updated[field] = value;
            } else if (updated[field] !== undefined) {
                delete updated[field];
            }

            if (Format.isEqual(this.data[field], value) == false) {
                this.data[field] = value;
            }
        }

        if (Object.keys(updated).length > 0) {
            this.updated = updated;
            this.store.updatedRecords.set(recordId, this);
        } else {
            this.updated = null;
            if (this.store.updatedRecords.has(recordId)) {
                this.store.updatedRecords.delete(recordId);
            }
        }

        await this.store.onEdit(this, is_fire_event);

        this.onUpdate(updated);
    }

    /**
     * PrimaryKey 에 해당하는 데이터를 가져온다.
     *
     * @returns PrimaryKey 데이터
     */
    getPrimaryData(): { [key: string]: any } | any {
        if (this.primaryKeys === null) {
            return this.getData();
        }

        const keys = Object.keys(this.primaryKeys);
        if (keys.length == 1) {
            return this.data[keys[0]] ?? null;
        } else {
            const data: { [key: string]: any } = {};
            for (const key of keys) {
                data[key] = this.data[key] ?? null;
            }

            return data;
        }
    }

    /**
     * 자식레코드를 불러온다.
     *
     * @returns 성공여부
     */
    async loadChildren(): Promise<boolean> {
        if (this.children === false) {
            return false;
        }

        const children: { [key: string]: any }[] = [];
        if (this.store.provider.childrenLoader !== null) {
            children.push(...(await this.store.provider.childrenLoader(this)));
        } else {
            if (this.store.provider instanceof LocalProvider) {
                return false;
            } else {
                const params = await this.store.provider.getLoaderParams();
                delete params['start'];
                delete params['limit'];

                let url = this.store.provider.url;
                if (this.store.provider.childrenProvider !== null) {
                    const { url: childUrl = '', params: childParam = {} } = this.store.provider.childrenProvider(this);

                    url = childUrl;
                    params['parent'] = JSON.stringify(childParam);
                } else {
                    let parent: string = '';
                    if (this.primaryKeys !== null) {
                        parent = JSON.stringify(this.primaryKeys);
                    } else {
                        parent = JSON.stringify(this.data);
                    }
                    params['parent'] = parent;
                }

                this.childLoaderId = UUID.v5('d723a13e-7796-58a2-b5d4-c8ac60759c70', JSON.stringify(params));

                if (this.childLoaders.has(this.childLoaderId)) {
                    children.push(...(await this.childLoaders.get(this.childLoaderId)));
                } else {
                    this.childLoaders.set(
                        this.childLoaderId,
                        (async (provider) => {
                            const results =
                                this.childLoaderResults.get(this.childLoaderId) ?? (await Ajax.get(url, params));
                            this.childLoaderResults.set(this.childLoaderId, { ...results });

                            if (results.success == true) {
                                if (
                                    results[provider.recordsField] !== undefined &&
                                    Array.isArray(results[provider.recordsField]) == true
                                ) {
                                    return results[provider.recordsField];
                                } else {
                                    return [];
                                }
                            }
                        })(this.store.provider)
                    );

                    children.push(...(await this.childLoaders.get(this.childLoaderId)));
                }
            }
        }

        this.children = new Map();
        this.childIds = [];
        for (const childData of children) {
            const child = this.store.set(childData, false);
            child.setParent(this);
            this.children.set(child.getId(), child);

            if ((await child.isFiltered(this.filters, this.filterMode, false)) == true) {
                this.childIds.push(child.getId());
            }
        }

        await this.#sort(false);
        return true;
    }

    /**
     * 자식이 존재하는지 확인한다.
     *
     * @param is_loaded - 자식을 실제로 불러왔는지도 확인할지 여부
     * @returns 자식존재여부
     */
    hasChildren(is_loaded: boolean = false): boolean {
        if (is_loaded == true) {
            if (this.children instanceof Map) {
                return true;
            } else {
                return false;
            }
        }

        return this.children !== false;
    }

    /**
     * 자식 레코드 목록을 가져온다.
     *
     * @param is_origin_children - 원본레코드 반환여부
     * @returns 자식 레코드 목록
     */
    async getChildren(is_origin_children: boolean = false): Promise<Record[]> {
        if (this.children instanceof Map) {
            const children = this.children;
            if (is_origin_children == true) {
                return children.values().toArray();
            } else {
                return (this.childIds ?? []).map((id: string) => children.get(id));
            }
        } else {
            if ((await this.loadChildren()) == true) {
                return await this.getChildren(is_origin_children);
            }
        }

        return [];
    }

    /**
     * 변경사항을 가져온다.
     *
     * @param  field - 변경사항을 가져올 데이터키 (NULL 인 경우 전체 변경사항을 가져온다.)
     * @return 변경사항
     */
    getUpdated(field: string = null): { [key: string]: any } | null {
        let updated = {};
        if (field === null) {
            updated = { ...this.updated };
        } else {
            if (this.updated[field] !== undefined) {
                updated = { key: this.updated[field] };
            } else {
                updated = null;
            }
        }

        return updated;
    }

    /**
     * 변경된 데이터를 커밋한다.
     *
     * @param field - 특정 키만 커밋할 경우 (NULL 인 경우 전체 데이터를 커밋한다.)
     */
    commit(field: string = null): void {
        if (this.isUpdated(field) === false) {
            return;
        }

        const recordId = this.getId();
        let updated = {};
        if (field === null) {
            this.origin = { ...this.data };
            updated = { ...this.updated };
            this.updated = null;

            this.store.updatedRecords.delete(recordId);
        } else {
            this.origin[field] = this.data[field];
            updated[field] = this.origin[field];
            delete this.updated[field];

            if (Object.keys(this.updated).length == 0) {
                this.updated = null;
                this.store.updatedRecords.delete(recordId);
            } else {
                this.store.updatedRecords.set(recordId, this);
            }
        }

        this.onUpdate(updated);
    }

    /**
     * 변경된 데이터를 롤백한다.
     *
     * @param field - 특정 키만 롤백할 경우 (NULL 인 경우 전체 데이터를 롤백한다.)
     */
    rollback(field: string = null): void {
        if (this.isUpdated(field) === false) {
            return;
        }

        const recordId = this.getId();
        let updated = {};
        if (field === null) {
            this.data = { ...this.origin };
            updated = { ...this.origin };
            this.updated = null;

            this.store.updatedRecords.delete(recordId);
        } else {
            this.data[field] = this.origin[field];
            updated[field] = this.data[field];
            delete this.updated[field];

            if (Object.keys(this.updated).length == 0) {
                this.updated = null;
                this.store.updatedRecords.delete(recordId);
            } else {
                this.store.updatedRecords.set(recordId, this);
            }
        }

        this.onUpdate(updated);
    }

    /**
     * 데이터가 변경된 상태인지 확인한다.
     *
     * @param field - 변경된 상태를 확인할 컬럼명 (NULL 인 경우 레코드 전체의 변경여부를 반환한다.)
     * @returns 변경여부
     */
    isUpdated(field: string = null): boolean {
        if (this.updated === null) {
            return false;
        }

        if (field === null) {
            return this.updated !== null;
        }

        if (this.updated[field] !== undefined) {
            return true;
        }

        return false;
    }

    /**
     * 필터조건에 해당하는지 확인한다.
     *
     * @param filters - 필터조건
     * @returns 필터결과
     */
    async isFiltered(
        filters: { [field: string]: { value: any; operator: string } } = null,
        filterMode: 'OR' | 'AND' = 'AND',
        is_fire_event: boolean = true
    ): Promise<boolean> {
        this.filters = { ...filters };
        this.filterMode = filterMode;

        let filtered: boolean = false;

        if (filters === null) {
            filtered = true;
        } else {
            filtered = Format.filter(this.getData(), filters, filterMode) == true;
        }

        if (this.hasChildren(true) == true) {
            const originIds = [...this.childIds];
            this.childIds = [];
            const children = await this.getChildren(true);
            for (const child of children) {
                if ((await child.isFiltered(filters, filterMode, true)) == true) {
                    this.childIds.push(child.getId());
                }
            }

            await this.#sort(false);

            if (is_fire_event == true && Format.isEqual(originIds, this.childIds, true) == false) {
                this.onUpdate(null, true);
            }
        } else if (this.hasChildren() == true) {
            filtered = true;
        }

        return filtered == true || this.childIds?.length > 0;
    }

    /**
     * 자식 레코드의 특정필드의 값을 자식 정렬인덱스(0부터 시작)으로 정리한다.
     *
     * @param field - 정렬순서를 입력할 레코드필드명
     */
    async reindex(field: string): Promise<void> {
        if (this.hasChildren(true) == false) {
            return;
        }

        const children = await this.getChildren(false);
        children.forEach(async (child, index) => {
            await child.set(field, index, false);
            await child.reindex(field);
        });
    }

    /**
     * 자식 레코드 목록을 정렬한다.
     *
     * @param sorters - 정렬방식
     */
    async sort(sorters: { [field: string]: 'ASC' | 'DESC' | any[] }): Promise<void> {
        if (Format.isEqual(this.sorters, sorters, true) == true) {
            return;
        }

        this.sorters = sorters === null ? null : { ...sorters };
        if (this.sorters === null) {
            return;
        }

        await this.#sort();
    }

    /**
     * 레코드 깊이를 가져온다.
     *
     * @returns 레코드 깊이
     */
    getDepth(): number {
        if (this.depth != null) return this.depth;

        let depth = 0;
        let parent = this.getParent();
        while (parent !== null) {
            depth++;
            parent = parent.getParent();
        }

        this.depth = depth;

        return depth;
    }

    /**
     * 현재 레코드가 특정 데이터와 일치하는지 확인한다.
     *
     * @param target - 일치여부를 확인할 레코드 또는 특정 데이터
     * @return 일치여부
     */
    isEqual(target: Record | { [key: string]: any }): boolean {
        let record: { [key: string]: any } = null;
        if (target instanceof Record) {
            record = target.getData();
        } else {
            record = target;
        }

        let keys = Object.keys(this.primaryKeys ?? {});
        if (keys.length == 0) {
            keys = Object.keys(this.getData());
        }

        for (const key of keys) {
            if (record[key] === undefined || Format.isEqual(record[key], this.get(key)) == false) {
                return false;
            }
        }

        return true;
    }

    /**
     * 데이터 변경 후 실행할 함수를 설정한다.
     *
     * @param observer - 데이터 변경 후 실행할 함수
     */
    setObserver(observer: (record: Record, fields: string[], tree: boolean) => void): void {
        this.observer = observer;
    }

    /**
     * 데이터가 변경되었음을 전파한다.
     *
     * @param updated - 변경된 데이터
     * @param is_children_updated - 자식이 변경되었는지 여부
     */
    onUpdate(updated: { [key: string]: any } = null, is_children_updated: boolean = false): void {
        if (this.observer !== null) {
            const fields = Object.keys(updated ?? {});
            this.observer(this, fields, is_children_updated);
        }
    }

    /**
     * 레코드를 제거한다.
     */
    destroy(): void {
        if (this.children instanceof Map) {
            for (const child of this.children.values()) {
                this.store?.records.delete(child.getId());
                child.destroy();
            }
            this.children.clear();
        }
        this.childLoaders.clear();
        this.childLoaderResults.clear();
        this.observer = null;
        this.parent = null;
        this.store = null;
    }

    /**
     * 자식 레코드 목록을 정렬한다.
     *
     * @param in_fire_event - 이벤트발생여부
     */
    async #sort(is_fire_event: boolean = true): Promise<void> {
        if (this.sorters === null) {
            return;
        }

        if (this.hasChildren(true) == true) {
            const chunks = new Chunks<Record>(await this.getChildren());
            const children = await chunks.sort((left, right) => {
                return Format.sorter(this.sorters, left.getData(), right.getData());
            });

            const originIds = [...this.childIds];
            this.childIds = [];
            const promises: Promise<void>[] = [];
            for (const child of children) {
                this.childIds.push(child.id);
                promises.push(child.sort(this.sorters));
            }
            await Promise.all(promises);

            if (is_fire_event == true && Format.isEqual(originIds, this.childIds, true) == false) {
                this.onUpdate([]);
            }
        }
    }
}
